/**
 * Voxel data packing/unpacking utilities and coordinate helpers
 */
import { CHUNK_SIZE, CHUNK_WORLD_SIZE, VOXEL_SCALE, WEIGHT_MIN, WEIGHT_RANGE, WEIGHT_MAX_PACKED, WEIGHT_SHIFT, WEIGHT_MASK, MATERIAL_SHIFT, MATERIAL_MASK, MATERIAL_MAX, LIGHT_MASK, LIGHT_MAX, BLOCK_LIGHT_SHIFT, BLOCK_LIGHT_MASK, SURFACE_PACKED_THRESHOLD, } from './constants.js';
import { MATERIAL_TYPE_LUT } from '../materials/Materials.js';
// ============== Voxel Packing/Unpacking ==============
/**
 * Pack weight, material, and (sky) light into a voxel word.
 * The block-light field (bit 16) is left 0 — block light is stamped separately
 * by the lighting pass, not at pack time.
 * @param weight - Weight value from -0.5 to +0.5 (surface at 0)
 * @param material - Material ID from 0 to 127
 * @param light - Sky light level from 0 to 31
 * @returns Packed voxel word (low 16 bits)
 */
export function packVoxel(weight, material, light) {
    // Normalize weight from [-0.5, 0.5] to [0, 15]
    const normalizedWeight = (weight - WEIGHT_MIN) / WEIGHT_RANGE;
    const packedWeight = Math.round(normalizedWeight * WEIGHT_MAX_PACKED);
    // Clamp values to valid ranges
    const clampedWeight = Math.max(0, Math.min(WEIGHT_MAX_PACKED, packedWeight));
    const clampedMaterial = Math.max(0, Math.min(MATERIAL_MAX, material | 0));
    const clampedLight = Math.max(0, Math.min(LIGHT_MAX, light | 0));
    // Pack: WWWW MMMMMMM LLLLL
    return (clampedWeight << WEIGHT_SHIFT) | (clampedMaterial << MATERIAL_SHIFT) | clampedLight;
}
/**
 * Unpack a 16-bit voxel value into weight, material, and light.
 * @param packed - Packed 16-bit voxel value
 * @returns Object with weight (-0.5 to +0.5), material (0-127), and light (0-31)
 */
export function unpackVoxel(packed) {
    return {
        weight: getWeight(packed),
        material: getMaterial(packed),
        light: getLight(packed),
        blockLight: getBlockLight(packed),
    };
}
/** Sky-light bits (internal — unpackVoxel's accessor; was a dead export). */
function getLight(packed) {
    return packed & LIGHT_MASK;
}
/** Block-light bits (internal — unpackVoxel's accessor; was a dead export). */
function getBlockLight(packed) {
    return (packed >>> BLOCK_LIGHT_SHIFT) & BLOCK_LIGHT_MASK;
}
/**
 * Extract weight from packed voxel, returning value in range [-0.5, +0.5].
 */
export function getWeight(packed) {
    const packedWeight = (packed >> WEIGHT_SHIFT) & WEIGHT_MASK;
    // Convert from [0, 15] back to [-0.5, 0.5]
    return (packedWeight / WEIGHT_MAX_PACKED) * WEIGHT_RANGE + WEIGHT_MIN;
}
/**
 * Extract material ID from packed voxel (0-127).
 */
export function getMaterial(packed) {
    return (packed >> MATERIAL_SHIFT) & MATERIAL_MASK;
}
/**
 * Check if a packed voxel is solid (weight > 0).
 * Solid voxels are inside terrain/objects.
 */
/**
 * The surface weight for a point `dist` voxels inside a surface, on a lattice of `stride` fine voxels.
 *
 * THE RAMP IS MEASURED IN THE LATTICE'S OWN VOXELS, and that is the whole content of this function: a
 * surface one COARSE voxel deep reads fully solid, so the ±2-voxel band SurfaceNets interpolates is the
 * same band at every level. Measuring in fine voxels instead saturates within one coarse voxel and
 * terraces the surface; ramping over metres leaves every coarse surface in a near-zero crossing band,
 * which reads as soft and stepped (measured on the tree stand-ins: trunks packed at 0.23-0.30 and an
 * oak's lod-3 crown at 0.03, against a solid 0.50). Written out at four sites in the coarse generator
 * and one in the parts rasterizer before it had a name.
 */
export function coarseWeight(dist, stride) {
    return Math.max(-0.5, Math.min(0.5, (dist / stride) * 0.5));
}
export function isVoxelSolid(packed) {
    // Weight bits > threshold means weight > 0 (solid)
    // This avoids floating point conversion for performance
    const packedWeight = (packed >> WEIGHT_SHIFT) & WEIGHT_MASK;
    return packedWeight > SURFACE_PACKED_THRESHOLD;
}
/**
 * Fast check whether a packed voxel data array contains any surface crossings.
 * A surface exists when:
 *  1. There are both "inside" (solid) and "outside" (air) voxels, OR
 *  2. Solid voxels have mixed material types (solid/transparent/liquid),
 *     because the SurfaceNet's material-type splitting creates surfaces
 *     between different material types (e.g. terrain vs water).
 *
 * Uses raw bit operations on the packed data — no float conversion.
 * Short-circuits as soon as a crossing is detected.
 *
 * @param data Flat packed voxel data (Uint32Array)
 * @returns true if a surface crossing exists in the data
 */
export function hasSurfaceCrossing(data) {
    let hasInside = false;
    let hasOutside = false;
    // Track first material type seen among solid voxels (-1 = none yet)
    let solidMatType = -1;
    const len = data.length;
    for (let i = 0; i < len; ++i) {
        const v = data[i];
        if (((v >> WEIGHT_SHIFT) & WEIGHT_MASK) > SURFACE_PACKED_THRESHOLD) {
            hasInside = true;
            if (hasOutside)
                return true;
            // Check for mixed material types among solid voxels
            const matId = (v >> MATERIAL_SHIFT) & MATERIAL_MASK;
            const mt = MATERIAL_TYPE_LUT[matId];
            if (solidMatType === -1) {
                solidMatType = mt;
            }
            else if (mt !== solidMatType) {
                return true; // Mixed material types → surface between them
            }
        }
        else {
            hasOutside = true;
            if (hasInside)
                return true;
        }
    }
    return false;
}
/**
 * Create a new packed voxel with updated weight, preserving material and light.
 * Uses bit ops — clears weight bits and sets new ones without touching material/light.
 */
export function setWeight(packed, weight) {
    const normalizedWeight = (weight - WEIGHT_MIN) / WEIGHT_RANGE;
    const packedWeight = Math.max(0, Math.min(WEIGHT_MAX_PACKED, Math.round(normalizedWeight * WEIGHT_MAX_PACKED)));
    return (packed & ~(WEIGHT_MASK << WEIGHT_SHIFT)) | (packedWeight << WEIGHT_SHIFT);
}
/**
 * Create a new packed voxel with updated material, preserving weight and light.
 * Uses bit ops — clears material bits and sets new ones without touching weight/light.
 */
export function setMaterial(packed, material) {
    const clampedMat = Math.max(0, Math.min(MATERIAL_MAX, material | 0));
    return (packed & ~(MATERIAL_MASK << MATERIAL_SHIFT)) | (clampedMat << MATERIAL_SHIFT);
}
// ============== Coordinate Conversions ==============
/**
 * Convert world coordinates to chunk coordinates.
 * Chunks are 8m x 8m x 8m, centered at origin (negative coords allowed).
 */
export function worldToChunk(x, y, z) {
    return {
        cx: Math.floor(x / CHUNK_WORLD_SIZE),
        cy: Math.floor(y / CHUNK_WORLD_SIZE),
        cz: Math.floor(z / CHUNK_WORLD_SIZE),
    };
}
/**
 * Global voxel coordinates → world coordinates of the voxel CENTRE (the +0.5 is the point of
 * this function — a corner-expecting caller off by half a voxel was the documented trap, so the
 * name now says which convention you get; use voxelCornerToWorld for the min corner).
 */
export function voxelCentreToWorld(vx, vy, vz) {
    return {
        x: (vx + 0.5) * VOXEL_SCALE,
        y: (vy + 0.5) * VOXEL_SCALE,
        z: (vz + 0.5) * VOXEL_SCALE,
    };
}
/**
 * Global voxel coordinates → world coordinates of the voxel's MIN CORNER (the lattice point —
 * what worldToChunk/voxelIndex-style floor conversions invert exactly).
 */
export function voxelCornerToWorld(vx, vy, vz) {
    return {
        x: vx * VOXEL_SCALE,
        y: vy * VOXEL_SCALE,
        z: vz * VOXEL_SCALE,
    };
}
/**
 * Convert local voxel coordinates (within a chunk) to flat array index.
 * Uses Y-up layout: index = x + y * CHUNK_SIZE + z * CHUNK_SIZE * CHUNK_SIZE
 */
export function voxelIndex(x, y, z) {
    return x + y * CHUNK_SIZE + z * CHUNK_SIZE * CHUNK_SIZE;
}
/**
 * Create a unique string key for a chunk coordinate (for use in Maps).
 */
export function chunkKey(cx, cy, cz) {
    return `${cx},${cy},${cz}`;
}
/**
 * Create a unique string key for a chunk coordinate at a LOD level.
 *
 * LOD 0 keys are byte-identical to `chunkKey` output ("cx,cy,cz") so every existing LOD-0
 * consumer (server protocol, IndexedDB persistence, build/relight/BFS paths) is unaffected;
 * coarse chunks get a 4th component ("cx,cy,cz,lod") and so can never collide with fine keys.
 */
export function chunkKeyLod(cx, cy, cz, lod) {
    return lod === 0 ? `${cx},${cy},${cz}` : `${cx},${cy},${cz},${lod}`;
}
/**
 * Parse a chunk key back to coordinates. Keys without a LOD component parse as lod 0.
 */
export function parseChunkKey(key) {
    const parts = key.split(',');
    return {
        cx: Number(parts[0]),
        cy: Number(parts[1]),
        cz: Number(parts[2]),
        lod: parts.length > 3 ? Number(parts[3]) : 0,
    };
}
//# sourceMappingURL=voxelData.js.map